// GENERATED by server/scripts/gen-ladder.mts from server/src/ladder.ts — do not edit; run `npm run ladder -w server`.
/**
 * The ladder (v1 spec, "One card engine"): the one DOM-free table of card
 * steps 0–4, the reading-and-typing and listening-and-speaking version of
 * each step, the card types each step uses and the retreat order, plus the
 * pure rules that read it.
 *
 * Single source. The server holds it because the server enforces the box
 * rules; `npm run ladder -w server` (run by the root build and typecheck)
 * copies this file verbatim to `web/src/ladder.generated.ts`. Keep it free of
 * imports and side effects so the copy compiles on both sides.
 */

/** A rung: 0 first look (fog), 1 rebuild, 2 recall, 3 use it somewhere new, 4 keep. */
export type Step = 0 | 1 | 2 | 3 | 4;

/** The two versions of every step. */
export type Version = 'read' | 'listen';
export const VERSIONS: readonly Version[] = ['read', 'listen'];

/**
 * Card types the ladder places: today's catalog and the ones tickets 11 and
 * 14 add (spot it, sound-alike pairs, say it from the meaning, dictation).
 * `listen` is two cards: pick its meaning from its line's audio (step 0), or
 * catch it by ear from the word alone, no text (step 1).
 */
export type LadderCard =
  | 'spotit' | 'choice' | 'reply' | 'reorder' | 'listen'
  | 'gapfill' | 'soundalike' | 'recall' | 'sayfrommeaning' | 'dictation';

export interface LadderRung {
  step: Step;
  name: string;
  /** What a word at this step is built of in the town. */
  material: 'fog' | 'scaffolding' | 'wood' | 'stone' | 'gold';
  /** Where the card asks the word. */
  setting: 'own line' | 'meaning' | 'somewhere new' | 'any';
  /** The card types of each version, the step's main card first. */
  cards: Readonly<Record<Version, readonly LadderCard[]>>;
  /** A pass that climbs from this step lands in `box`, due `days` later. Step 4 stays in box 4. */
  pass: { box: number; days: number };
  /**
   * The step a scored miss retreats to: one down, the same word and version.
   * Step 1 falls back to choosing (step 0); step 0 has nowhere to go. A keep
   * card (step 4) is one of steps 1–3 and retreats from the step it was.
   */
  retreat: Step | null;
}

export const LADDER: readonly LadderRung[] = [
  {
    step: 0, name: 'first look', material: 'fog', setting: 'own line',
    cards: { read: ['spotit', 'choice', 'reply', 'reorder'], listen: ['listen'] },
    pass: { box: 1, days: 1 }, retreat: null,
  },
  {
    step: 1, name: 'rebuild', material: 'scaffolding', setting: 'own line',
    cards: { read: ['gapfill'], listen: ['soundalike', 'listen'] },
    pass: { box: 2, days: 3 }, retreat: 0,
  },
  {
    step: 2, name: 'recall', material: 'wood', setting: 'meaning',
    cards: { read: ['recall'], listen: ['sayfrommeaning'] },
    pass: { box: 3, days: 7 }, retreat: 1,
  },
  {
    step: 3, name: 'use it somewhere new', material: 'stone', setting: 'somewhere new',
    cards: { read: ['gapfill', 'recall'], listen: ['dictation'] },
    pass: { box: 4, days: 14 }, retreat: 2,
  },
  {
    step: 4, name: 'keep', material: 'gold', setting: 'any',
    cards: { read: ['gapfill', 'recall'], listen: ['soundalike', 'listen', 'sayfrommeaning', 'dictation'] },
    pass: { box: 4, days: 14 }, retreat: null,
  },
];

/**
 * The retreat order of each version, hardest card first: a scored miss
 * serves the next card down. Reorder is the supported retreat below a
 * gap-fill; choosing is the floor.
 */
export const RETREAT_ORDER: Readonly<Record<Version, readonly LadderCard[]>> = {
  read: ['recall', 'gapfill', 'reorder', 'choice'],
  listen: ['dictation', 'sayfrommeaning', 'soundalike', 'listen'],
};

/**
 * The version a card type belongs to: the ladder's listening cards and say it
 * back (speaking, off the ladder) are the listening-and-speaking version,
 * every other card reads.
 */
export function versionOf(type: string): Version {
  if (type === 'sayitback') return 'listen';
  return LADDER.some((r) => (r.cards.listen as readonly string[]).includes(type)) ? 'listen' : 'read';
}

/**
 * The step a scored miss at `step` retreats to (v1 ticket 04): one down;
 * step 1 falls back to choosing and step 0 is the floor. A keep card is
 * saved at the step it was (1–3), so step 4 retreats like step 3.
 */
export function retreatStep(step: Step): Step {
  return LADDER[Math.min(step, 3)].retreat ?? 0;
}

/** Days a word waits after landing in box 1–4 (index box − 1), read off the table. */
export const BOX_INTERVAL_DAYS: readonly number[] = LADDER.slice(0, 4).map((r) => r.pass.days);
export const MAX_BOX = 4;

/**
 * The review due time a word gets when it lands in `box` at `from` (calendar days, so DST
 * never shortens or stretches a wait). Shared by the live answer writer and the maintenance backfill.
 */
export function dueAfter(box: number, from = new Date()): string {
  const d = new Date(from);
  d.setDate(d.getDate() + BOX_INTERVAL_DAYS[Math.min(box, MAX_BOX) - 1]);
  return d.toISOString();
}

/**
 * Card types that never score: a flashcard flip is the first look's
 * presentation only. Attempt marks ("I said it", reveals, skips, disputes)
 * are the other self-marks; see {@link isScored}.
 */
export const SELF_MARK_CARDS: readonly string[] = ['flashcard'];

/**
 * The spelling card (ticket 05): a one-off typed card of a word whose spike a
 * scratch missed, served in the next round. The app checks it, but it is
 * saved unscored like a self-mark: its word already passed, so it never moves
 * a box and never counts as evidence.
 */
export const SPELLING_CARD = 'spelling';

/** Card types saved unscored whatever the answer. */
const UNSCORED_CARDS: readonly string[] = [...SELF_MARK_CARDS, SPELLING_CARD];

/** The step a word must pass to climb from its box. Box 4 keeps with any step 1–3 card. */
export function climbStep(box: number): Step {
  if (box <= 0) return 0;
  if (box >= MAX_BOX) return 1;
  return box as Step;
}

/**
 * Every step a card type can count as, lowest first. Gap-fill and recall
 * are also step 3 somewhere new, listen is also step 1 by ear. Card types
 * off the ladder (say it back) count as step 0; self-mark cards and the
 * spelling card have none.
 */
export function stepsOf(type: string): Step[] {
  if (UNSCORED_CARDS.includes(type)) return [];
  const steps = LADDER.filter((r) => r.step < 4 && VERSIONS.some((v) => r.cards[v].includes(type as LadderCard)))
    .map((r) => r.step);
  return steps.length ? steps : [0];
}

/**
 * The step of one card. `alone`: a listen card plays the word by itself, not
 * its line. `elsewhere`: a gap-fill or recall asked somewhere other than the
 * word's own line and meaning.
 */
export function cardStep(type: string, where: { alone?: boolean; elsewhere?: boolean } = {}): Step | null {
  const steps = stepsOf(type);
  if (!steps.length) return null;
  if (where.elsewhere && steps.includes(3)) return 3;
  if (type === 'listen') return where.alone ? 1 : 0;
  return steps[0];
}

/** How an attempt was marked, whatever the card. */
export interface AttemptMarks { type: string; selfReport?: boolean; revealed?: boolean; skipped?: boolean }

/**
 * Only answers the app checked are scored. Flashcard flips, "I said it",
 * reveals, every skip (disputes included) and the spelling card save
 * unscored: they never move a box and never count as evidence.
 */
export function isScored(a: AttemptMarks): boolean {
  return !a.selfReport && !a.revealed && !a.skipped && !UNSCORED_CARDS.includes(a.type);
}

/**
 * Whether a passed scored card climbs its word one box: only when the word
 * was due, the pass was unaided and the card's step is at or above the
 * word's climbing step. Every other pass keeps the box (and its due date).
 */
export function climbs(box: number, step: Step, opts: { due: boolean; hinted: boolean }): boolean {
  return opts.due && !opts.hinted && step >= climbStep(box);
}
